import {
  namedOperations,
  useAddYoutubeCompareAccountAnalyticsMutation,
  useDeleteYoutubeCompareAccountAnalyticsMutation
} from '@anytag/graphql';
import { useQueryHelper, useSelectItem } from '@anytag/shared-hooks';
import { AnalyticsYoutubeComparableAccountType } from '../../types';

interface Props {
  mainAccountId: number;
}

export const useCreateDeleteYoutubeComparableAccount = ({ mainAccountId }: Props) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const {
    selectedItem: deletingAccount,
    setSelectedItem: setDeletingAccount,
    resetSelectedItem: resetDeletingAccount
  } = useSelectItem<AnalyticsYoutubeComparableAccountType | null>();
  const { callAddYoutubeCompareAccountAnalytics, loading: adding } = useAddYoutubeCompareAccountAnalyticsMutation({
    refetchQueries: [namedOperations.Query.YoutubeAnalyticsCompareAccountCards]
  });
  const { callDeleteYoutubeCompareAccountAnalytics, loading: deleting } =
    useDeleteYoutubeCompareAccountAnalyticsMutation({
      refetchQueries: [namedOperations.Query.YoutubeAnalyticsCompareAccountCards]
    });

  const handleAddNewComparableAccount = async (url: string, callback?: () => void) => {
    if (!url) return;

    try {
      await callAddYoutubeCompareAccountAnalytics({ variables: { input: { url, mainAccountId } } });

      callback?.();
      enqueueSnackbar(t('succeededInCreating', { name: url }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message || 'failedToCreate'), { variant: 'error' });
    }
  };

  const handleDeleteComparableAccount = async () => {
    if (deletingAccount) {
      try {
        await callDeleteYoutubeCompareAccountAnalytics({
          variables: { input: { compareAccountId: deletingAccount.id, mainAccountId } }
        });
        enqueueSnackbar(t('succeededInDeleting', { name: deletingAccount.username }), { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(t(error.message || 'failedToDelete'), { variant: 'error' });
      } finally {
        setDeletingAccount(null);
      }
    }
  };

  return {
    adding,
    deleting,
    deletingAccount,
    setDeletingAccount,
    resetDeletingAccount,
    handleAddNewComparableAccount,
    handleDeleteComparableAccount
  };
};
